import { useState, useEffect, useCallback } from 'react';
import { Phone, Save, RefreshCw, ArrowLeft, Check, X, Plus } from 'lucide-react';
import api, { TrackingNumber } from '../services/api';
import { useClient } from '../contexts/ClientContext';
import { Link } from 'react-router-dom';

interface NewNumberForm {
  phone_e164: string;
  forward_to: string;
  purpose: string;
}

const emptyNewForm: NewNumberForm = {
  phone_e164: '',
  forward_to: '',
  purpose: 'main',
};

const PhoneSettings = () => {
  const { selectedClientId, selectedClient } = useClient();
  const [trackingNumbers, setTrackingNumbers] = useState<TrackingNumber[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState({ forward_to: '', is_active: true });
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newForm, setNewForm] = useState<NewNumberForm>(emptyNewForm);
  const [creating, setCreating] = useState(false);

  const loadTrackingNumbers = useCallback(async () => {
    if (!selectedClientId) {
      setTrackingNumbers([]);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const data = await api.getTrackingNumbers(selectedClientId);
      setTrackingNumbers(data);
    } catch (err) {
      console.error('Failed to load tracking numbers:', err);
      setError('Failed to load tracking numbers');
    } finally {
      setLoading(false);
    }
  }, [selectedClientId]);

  useEffect(() => {
    loadTrackingNumbers();
  }, [loadTrackingNumbers]);

  const startEditing = (tn: TrackingNumber) => {
    setEditingId(tn.id);
    setEditForm({
      forward_to: tn.forward_to || '',
      is_active: tn.is_active,
    });
    setSaveSuccess(null);
    setShowAddForm(false);
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditForm({ forward_to: '', is_active: true });
  };

  const saveChanges = async (id: string) => {
    setSaving(true);
    setSaveSuccess(null);
    try {
      await api.updateTrackingNumber(id, editForm);
      setEditingId(null);
      setSaveSuccess(id);
      loadTrackingNumbers();
      setTimeout(() => setSaveSuccess(null), 3000);
    } catch (err) {
      console.error('Failed to save tracking number:', err);
      setError('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateNumber = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedClientId) return;

    // Validate
    if (!newForm.phone_e164 || !newForm.forward_to) {
      setError('Please fill in both phone number and forward-to number');
      return;
    }

    // Normalize phone numbers to E.164
    const normalizePhone = (phone: string): string => {
      let cleaned = phone.replace(/\D/g, '');
      if (cleaned.length === 10) {
        cleaned = '1' + cleaned;
      }
      if (!cleaned.startsWith('+')) {
        cleaned = '+' + cleaned;
      }
      return cleaned;
    };

    setCreating(true);
    setError(null);

    try {
      const phoneE164 = normalizePhone(newForm.phone_e164);
      const forwardTo = normalizePhone(newForm.forward_to);

      await api.createTrackingNumber({
        client: selectedClientId,
        phone_e164: phoneE164,
        forward_to: forwardTo,
        purpose: newForm.purpose,
      });

      setShowAddForm(false);
      setNewForm(emptyNewForm);
      loadTrackingNumbers();
    } catch (err: any) {
      console.error('Failed to create tracking number:', err);
      setError(err?.message || 'Failed to create tracking number');
    } finally {
      setCreating(false);
    }
  };

  const formatPhone = (phone: string): string => {
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 11 && cleaned.startsWith('1')) {
      return `(${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
    }
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    return phone;
  };

  const purposeLabels: Record<string, string> = {
    main: 'Main Line',
    ads: 'Ads Tracking',
    landing: 'Landing Page',
    gbp: 'Google Business',
    referral: 'Referral',
  };

  if (!selectedClientId) {
    return (
      <div className="p-6">
        <div className="bg-yellow-50 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-200 p-4 rounded-lg">
          Please select a client to manage phone settings.
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link
            to="/calls"
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <Phone className="w-6 h-6" />
              Phone Settings
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              {selectedClient?.name || 'Client'} - Manage tracking numbers and forwarding
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              setShowAddForm(true);
              setEditingId(null);
            }}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-black rounded-lg hover:bg-primary/90 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add Number
          </button>
          <button
            onClick={loadTrackingNumbers}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200 p-4 rounded-lg flex items-center justify-between">
          {error}
          <button onClick={() => setError(null)} className="hover:opacity-70">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Add New Number Form */}
      {showAddForm && (
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border-2 border-primary">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Add Tracking Number
          </h3>
          <form onSubmit={handleCreateNumber} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Plivo Phone Number *
                </label>
                <input
                  type="tel"
                  value={newForm.phone_e164}
                  onChange={(e) => setNewForm({ ...newForm, phone_e164: e.target.value })}
                  placeholder="(904) 717-8677"
                  className="w-full px-3 py-2 bg-gray-100 dark:bg-gray-700 rounded-lg border-0 focus:ring-2 focus:ring-primary"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  The number you purchased in Plivo
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Forward To *
                </label>
                <input
                  type="tel"
                  value={newForm.forward_to}
                  onChange={(e) => setNewForm({ ...newForm, forward_to: e.target.value })}
                  placeholder="(555) 123-4567"
                  className="w-full px-3 py-2 bg-gray-100 dark:bg-gray-700 rounded-lg border-0 focus:ring-2 focus:ring-primary"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  Your personal phone to receive calls
                </p>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Purpose
              </label>
              <select
                value={newForm.purpose}
                onChange={(e) => setNewForm({ ...newForm, purpose: e.target.value })}
                className="w-full max-w-xs px-3 py-2 bg-gray-100 dark:bg-gray-700 rounded-lg border-0 focus:ring-2 focus:ring-primary"
              >
                <option value="main">Main Line</option>
                <option value="ads">Ads Tracking</option>
                <option value="landing">Landing Page</option>
                <option value="gbp">Google Business</option>
                <option value="referral">Referral</option>
              </select>
            </div>
            <div className="flex items-center gap-2 pt-2">
              <button
                type="submit"
                disabled={creating}
                className="flex items-center gap-2 px-4 py-2 bg-primary text-black rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50"
              >
                {creating ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <Plus className="w-4 h-4" />
                )}
                Add Number
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowAddForm(false);
                  setNewForm(emptyNewForm);
                }}
                className="px-4 py-2 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>

          {/* Plivo Setup Instructions */}
          <div className="mt-4 p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
            <h4 className="font-medium text-yellow-800 dark:text-yellow-200 mb-2">
              Plivo Dashboard Setup Required
            </h4>
            <p className="text-sm text-yellow-700 dark:text-yellow-300 mb-2">
              After adding, configure these webhooks in your Plivo dashboard for the number:
            </p>
            <ul className="text-sm text-yellow-700 dark:text-yellow-300 space-y-1 font-mono">
              <li>Answer URL: https://api.digimint.co/api/v1/telephony/plivo/answer/</li>
              <li>Hangup URL: https://api.digimint.co/api/v1/telephony/plivo/call_event/</li>
            </ul>
          </div>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
        </div>
      )}

      {/* Tracking Numbers List */}
      {!loading && (
        <div className="space-y-4">
          {trackingNumbers.length === 0 && !showAddForm ? (
            <div className="bg-white dark:bg-gray-800 rounded-lg p-8 text-center">
              <Phone className="w-12 h-12 mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                No tracking numbers
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                Add a tracking number to start tracking calls.
              </p>
              <button
                onClick={() => setShowAddForm(true)}
                className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-black rounded-lg hover:bg-primary/90 transition-colors"
              >
                <Plus className="w-4 h-4" />
                Add Your First Number
              </button>
            </div>
          ) : (
            trackingNumbers.map((tn) => (
              <div
                key={tn.id}
                className={`bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm ${
                  saveSuccess === tn.id ? 'ring-2 ring-green-500' : ''
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-xl font-semibold text-gray-900 dark:text-white">
                        {formatPhone(tn.phone_e164)}
                      </span>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        tn.is_active
                          ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                      }`}>
                        {tn.is_active ? 'Active' : 'Inactive'}
                      </span>
                      <span className="px-2 py-0.5 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded-full text-xs font-medium">
                        {purposeLabels[tn.purpose] || tn.purpose}
                      </span>
                    </div>

                    {editingId === tn.id ? (
                      <div className="space-y-4 mt-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Forward calls to
                          </label>
                          <input
                            type="tel"
                            value={editForm.forward_to}
                            onChange={(e) => setEditForm({ ...editForm, forward_to: e.target.value })}
                            placeholder="+1XXXXXXXXXX"
                            className="w-full max-w-xs px-3 py-2 bg-gray-100 dark:bg-gray-700 rounded-lg border-0 focus:ring-2 focus:ring-primary"
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            Enter in E.164 format (e.g., +19047178677)
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            id={`active-${tn.id}`}
                            checked={editForm.is_active}
                            onChange={(e) => setEditForm({ ...editForm, is_active: e.target.checked })}
                            className="w-4 h-4 rounded text-primary focus:ring-primary"
                          />
                          <label htmlFor={`active-${tn.id}`} className="text-sm text-gray-700 dark:text-gray-300">
                            Number is active
                          </label>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => saveChanges(tn.id)}
                            disabled={saving}
                            className="flex items-center gap-2 px-4 py-2 bg-primary text-black rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50"
                          >
                            {saving ? (
                              <RefreshCw className="w-4 h-4 animate-spin" />
                            ) : (
                              <Save className="w-4 h-4" />
                            )}
                            Save
                          </button>
                          <button
                            onClick={cancelEditing}
                            className="px-4 py-2 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="mt-2">
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                          <span className="font-medium">Forwards to:</span>{' '}
                          {tn.forward_to ? formatPhone(tn.forward_to) : 'Not configured'}
                        </div>
                        <div className="flex items-center gap-4 mt-2 text-sm text-gray-500 dark:text-gray-500">
                          <span>{tn.total_calls} calls</span>
                          <span>{tn.total_sms} SMS</span>
                        </div>
                      </div>
                    )}
                  </div>

                  {editingId !== tn.id && (
                    <div className="flex items-center gap-2">
                      {saveSuccess === tn.id && (
                        <span className="flex items-center gap-1 text-green-600 text-sm">
                          <Check className="w-4 h-4" />
                          Saved
                        </span>
                      )}
                      <button
                        onClick={() => startEditing(tn)}
                        className="px-4 py-2 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors text-sm"
                      >
                        Edit
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Info Card */}
      <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
        <h3 className="font-medium text-blue-800 dark:text-blue-200 mb-2">How Call Forwarding Works</h3>
        <ul className="text-sm text-blue-700 dark:text-blue-300 space-y-1">
          <li>• Calls to your tracking number are forwarded to the configured number</li>
          <li>• If not answered within 25 seconds, callers can leave a voicemail</li>
          <li>• All calls are recorded and logged in the Call History</li>
          <li>• You'll receive SMS/email notifications for missed calls and voicemails</li>
        </ul>
      </div>
    </div>
  );
};

export default PhoneSettings;
